import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import {
  buildTimezoneOption,
  filterTimezoneOptions,
  getTimezoneOptions,
} from "../lib/timezones";

const triggerCls = "app-control text-sm text-left flex items-center gap-2";

const searchCls =
  "w-full min-w-0 bg-[var(--surface-secondary)] border-0 border-b border-[var(--border-primary)] focus:border-brand-primary outline-none px-4 py-3 text-dark-text-primary text-sm";

const TimezoneCopy = ({ option, showOffset = false }) => (
  <span className="min-w-0 flex-1">
    <span className="block text-dark-text-primary break-words">{option.primaryLabel}</span>
    <span className="block text-[11px] font-mono text-dark-text-muted break-words">
      {showOffset ? option.secondaryLabel : option.value}
    </span>
  </span>
);

export const TimezoneSelect = ({
  value,
  onChange,
  disabled = false,
  placeholder = "Search city or timezone...",
}) => {
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const options = useMemo(() => {
    const catalog = getTimezoneOptions();
    if (value && !catalog.some((option) => option.value === value)) {
      return [buildTimezoneOption(value), ...catalog];
    }
    return catalog;
  }, [value]);

  const filtered = useMemo(
    () => filterTimezoneOptions(options, query),
    [options, query],
  );

  const selected = value
    ? options.find((option) => option.value === value) || buildTimezoneOption(value)
    : null;

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const frame = window.requestAnimationFrame(() => {
      searchRef.current?.focus();
      const selectedOption = listRef.current?.querySelector('[aria-selected="true"]');
      selectedOption?.scrollIntoView({ block: "nearest" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  const selectZone = (iana) => {
    onChange(iana);
    setOpen(false);
    setQuery("");
  };

  const onSearchKeyDown = (event) => {
    if (event.key === "Enter" && filtered.length === 1) {
      event.preventDefault();
      selectZone(filtered[0].value);
    }
  };

  return (
    <div className="relative min-w-0 w-full" ref={rootRef} data-testid="timezone-select">
      <button
        type="button"
        className={`${triggerCls} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        onClick={() => {
          if (disabled) return;
          setOpen((current) => {
            const next = !current;
            if (!next) setQuery("");
            return next;
          });
        }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={selected ? `Timezone ${selected.primaryLabel}` : "Timezone"}
        data-testid="input-timezone"
      >
        {selected ? (
          <TimezoneCopy option={selected} />
        ) : (
          <span className="min-w-0 flex-1 text-dark-text-muted">{placeholder}</span>
        )}
        <ChevronDown
          className={`w-4 h-4 shrink-0 text-dark-text-muted transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={1.5}
        />
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 left-0 right-0 w-full max-w-full min-w-0 bg-dark-surface-2 border border-dark-border shadow-lg overflow-hidden"
          data-testid="timezone-dropdown"
        >
          <input
            ref={searchRef}
            type="text"
            className={searchCls}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder="Search city or timezone..."
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            inputMode="search"
            enterKeyHint="search"
            aria-label="Search timezones"
            data-testid="timezone-search"
          />
          <ul
            ref={listRef}
            role="listbox"
            aria-label="Timezones"
            className="max-h-[min(15rem,45dvh)] overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y"
            data-testid="timezone-options"
          >
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-sm text-dark-text-muted">No matching timezones</li>
            ) : (
              filtered.map((option) => {
                const isSelected = option.value === value;
                return (
                  <li key={option.value} className="min-w-0">
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      data-testid={`timezone-option-${option.value}`}
                      data-iana={option.value}
                      className={`w-full min-w-0 min-h-[44px] px-4 py-3 text-left text-sm flex items-start gap-2 hover:bg-dark-surface-3 ${
                        isSelected ? "bg-dark-surface-3" : ""
                      }`}
                      onClick={() => selectZone(option.value)}
                    >
                      <TimezoneCopy option={option} showOffset />
                      {isSelected && (
                        <Check className="w-4 h-4 mt-0.5 shrink-0 text-brand-primary" strokeWidth={2} />
                      )}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
